import { Box, Grid, TextField, Typography, Button } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch } from "react-redux";
import { FooterNewStyles } from "./footerNew.styles";
import WanderobeFooterLogo from "../../../assets/images/wanderobe-logo-1.svg";
import GooglePlay from "../../../assets/images/googlePlayStoreSmallIcon.svg";
import AppStore from "../../../assets/images/appleStoreSmallIcon.svg";
import { HOME_PAGE, SOCIALMEDIA_URL } from "../../../constants/lang";
import { ROUTE_PATH } from "../../../constants/route";
import Facebook from "../../../assets/images/facebookSmallLogo.svg";
import Instagram from "../../../assets/images/instagramSmallLogo.svg";
import { newsletterSchema } from "../../../schema/newsletterSchema";
import { fetchNewsletter } from "../../../store/reducers/authentication/apiThunk";
import { ToastNotifySuccess, ToastNotifyError } from "../Toast/ToastNotify";
import { useState } from "react";
import { fetchSupportInformation } from "../../../store/reducers/authentication/apiThunk";
import LoadingOverlay from "../Loader/ShimmerLoader";

const styles = FooterNewStyles;

const FooterNew = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);

    const {
        control,
        handleSubmit,
        formState: { errors, isSubmitting },
        reset
    } = useForm({
        resolver: yupResolver(newsletterSchema),
        defaultValues: {
            email: ""
        }
    });

    const handleLogoClick = () => {
        navigate('/');
    };

    const handleGetInTouchClick = () => {
        getSupportInformation();
    };

    const getSupportInformation = async () => {
        setLoading(true);
        try {
          const res = await dispatch(fetchSupportInformation());
          const { status, message, data } = res?.payload;
          if (status) {
            const sellerSupportInfo = data.find(item => item.support_for === "seller");
            
            if (sellerSupportInfo) {
              window.open(`mailto:${sellerSupportInfo?.email}`, '_blank');
            } else {
              ToastNotifyError("No seller support information found");
            }
            setLoading(false);
          } else {
            ToastNotifyError(message);
            setLoading(false);
          }
        } catch (error) {
          setLoading(false);
          console.error("Error fetching support information:", error);
        }
      };  

    const handleNewsletterSubmit = async (data) => {
        try {
            const res = await dispatch(fetchNewsletter(data));
            const { status, message } = res?.payload;            
            if (status === true) {
                ToastNotifySuccess(message || "Successfully subscribed to newsletter!");
                reset();
            } else if (status === 400) {
                ToastNotifyError(res?.payload?.response?.data?.message || "Failed to subscribe. Please try again.");
            } else {
                ToastNotifyError(message || "Failed to subscribe. Please try again.");
            }
        } catch (error) {
            ToastNotifyError("An error occurred. Please try again.");
        }
    };
    return (
        <Box sx={styles.container}>
            {loading && <LoadingOverlay loading={loading} />}
            <Grid container>
                <Grid item xs={12} sm={4} sx={{ textAlign: "center", display: { xs: "block", sm: "none" } }}>
                    <Typography sx={styles.downloadText}>Download the Wanderobe App</Typography>
                    <Box sx={{ ...styles.downloadContainer, justifyContent: "center", mr: 'unset !important' }}>
                        <Box component="img" src={AppStore} alt="app-store" sx={styles.appStoreLogo} onClick={() => window.open(SOCIALMEDIA_URL.appleStore.url, '_blank')} />
                        <Box component="img" src={GooglePlay} alt="google-play" sx={styles.googlePlayLogo} onClick={() => window.open(SOCIALMEDIA_URL.playStore.url, '_blank')} />
                    </Box>
                </Grid>
                <Grid item xs={12} sm={4} sx={{ textAlign: { xs: "center", sm: "left" }, mt: { xs: "26.21px", sm: "unset" } }}>
                    <Box
                        sx={{
                            ...styles.getInTouch,
                            cursor: 'pointer',
                            width: 'fit-content',
                            mx: { xs: 'auto', sm: 'unset' },
                            '&:hover': {
                                transform: 'scale(1.05)',
                            }
                        }}
                        onClick={handleGetInTouchClick}
                    >
                        Get In Touch
                    </Box>
                    <Box
                        sx={{
                            ...styles.faq,
                            cursor: 'pointer',
                            width: 'fit-content',
                            mx: { xs: 'auto', sm: 'unset' },
                            '&:hover': {
                                transform: 'scale(1.05)',
                            }
                        }}
                        onClick={() => navigate('/customer-support')}
                    >
                        FAQ
                    </Box>
                </Grid>
                <Grid item xs={12} sm={4} sx={{ textAlign: "center", display: { xs: "none", sm: "block" } }}>
                    <Box
                        component="img"
                        src={WanderobeFooterLogo}
                        alt="footer-logo"
                        sx={{ ...styles.footerLogo, cursor: 'pointer' }}
                        onClick={handleLogoClick}
                    />
                    <Typography sx={styles.footerText}>Don’t just visit the city. Wear it home.</Typography>
                </Grid>
                <Grid item xs={12} sm={4} sx={{ textAlign: "right", display: { xs: "none", sm: "block" } }}>
                    <Typography sx={styles.downloadText}>Download the Wanderobe App</Typography>
                    <Box sx={styles.downloadContainer}>
                        <Box component="img" src={AppStore} alt="app-store" sx={styles.appStoreLogo} onClick={() => window.open(SOCIALMEDIA_URL.appleStore.url, '_blank')} />
                        <Box component="img" src={GooglePlay} alt="google-play" sx={styles.googlePlayLogo} onClick={() => window.open(SOCIALMEDIA_URL.playStore.url, '_blank')} />
                    </Box>
                </Grid>
                <Grid item xs={12} sm={12} sx={{ textAlign: { xs: "center", sm: "left" } }}>
                    <Typography sx={styles.emailText}>Enter your email for exclusive offers and updates</Typography>
                    <Box component="form" onSubmit={handleSubmit(handleNewsletterSubmit)} sx={styles.emailContainer}>
                        <Box sx={{ position: 'relative' }}>
                            <Controller
                                name="email"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        placeholder="Enter email"
                                        sx={{
                                            ...styles.emailInput,
                                            ...(errors.email && {
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        borderColor: '#d32f2f',
                                                    },
                                                },
                                            }),
                                        }}
                                        error={!!errors.email}
                                    />
                                )}
                            />
                            {errors.email && (
                                <Typography
                                    sx={{
                                        color: '#d32f2f',
                                        fontSize: '0.75rem',
                                        mt: 0.5,
                                        position: 'absolute',
                                        left: 0,
                                        top: '100%',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {errors.email.message}
                                </Typography>
                            )}
                        </Box>
                        <Button 
                            type="submit" 
                            variant="contained" 
                            sx={styles.subscribeButton}
                            disabled={isSubmitting}
                        >
                            Sign up
                        </Button>
                    </Box>
                </Grid>
                <Grid item xs={12} sm={2} sx={{ textAlign: "center", mt: '42.3px', display: { xs: "none", sm: "block" } }}></Grid>
                <Grid item xs={12} sm={4} sx={{ textAlign: "center", display: { xs: "block", sm: "none" }, mt: '31.63px' }}>
                    <Box
                        component="img"
                        src={WanderobeFooterLogo}
                        alt="footer-logo"
                        sx={{ ...styles.footerLogo, cursor: 'pointer' }}
                        onClick={handleLogoClick}
                    />
                    <Typography sx={styles.footerText}>Don’t just visit the city. Wear it home.</Typography>
                </Grid>
                <Grid item xs={12} sm={2} sx={{ textAlign: "center", mt: '15.71px', display: { xs: "block", sm: "none" } }}>
                    <Box sx={{ ...styles.socialContainer, justifyContent: "center", mr: 'unset !important', gap: '8.06px' }}>
                        <Box component="img" src={Instagram} alt="instagram" sx={styles.socialLogo} onClick={() => window.open(SOCIALMEDIA_URL.instagram, '_blank')} />
                        <Box component="img" src={Facebook} alt="facebook" sx={styles.socialLogo} onClick={() => window.open(SOCIALMEDIA_URL.facebook, '_blank')} />
                    </Box>
                </Grid>
                <Grid item xs={12} sm={8} sx={{ textAlign: "center", justifyContent: "center", alignItems: "center", gap:  { xs: '4px', sm: '10px'}, display: "flex", mt: { xs: '23.85px', sm: '42.3px' } }}>
                    <Typography sx={styles.copyrightText}>© 2026 Wanderobe LLC</Typography>
                    <Link style={styles.clickableText} target='_blank' to={ROUTE_PATH.TERMS_AND_CONDITION} aria-label={HOME_PAGE.footer.termsAriaLabel}>
                        <Typography sx={styles.pptc}>{HOME_PAGE.footer.terms}</Typography>
                    </Link>
                    <Link style={styles.clickableText} to={ROUTE_PATH.PRIVACY_POLICY} target='_blank' aria-label={HOME_PAGE.footer.privacyAriaLabel}>
                        <Typography sx={styles.pptc}>{HOME_PAGE.footer.privacy}</Typography>
                    </Link>
                </Grid>
                <Grid item xs={12} sm={2} sx={{ textAlign: "right", mt: '42.3px', display: { xs: "none", sm: "block" } }}>
                    <Box sx={styles.socialContainer}>
                        <Box component="img" src={Instagram} alt="instagram" sx={styles.socialLogo} onClick={() => window.open(SOCIALMEDIA_URL.instagram, '_blank')} />
                        <Box component="img" src={Facebook} alt="facebook" sx={styles.socialLogo} onClick={() => window.open(SOCIALMEDIA_URL.facebook, '_blank')} />
                    </Box>
                </Grid>
            </Grid>

        </Box>
    )
}

export default FooterNew;